import React, { useEffect, useMemo, useRef, useState } from "react";
import { installNotifyFeedbackUnlock, playNotifyFeedback } from "../utils/notifyFeedback";
import { useLocation } from "react-router-dom";
import {
  Crown,
  Plus,
  Search,
  Send,
  Users,
  Radio,
  Pin,
  TrendingUp,
  X,
  MessageCircle,
  UserPlus,
  Trash2,
  Settings,
  Save,
  Ban,
  CreditCard, CheckCircle, Clock, Camera, UserCircle, Menu
} from "lucide-react";

const TOKEN_KEY = "ct_login_token";
const USER_KEY = "ct_login_user";
const FACE_MODEL_URL = `${process.env.PUBLIC_URL || ""}/vendor/face-api/weights`;
let nexoFaceModelsPromise = null;

function getLoginUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

function timeText(date) {
  return date
    ? new Date(date).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
}

function getFaceApi() {
  return window.faceapi || window.faceApi || null;
}

async function waitForFaceApi() {
  for (let i = 0; i < 40; i++) {
    const faceapi = getFaceApi();
    if (faceapi?.nets?.tinyFaceDetector) return faceapi;
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  throw new Error("Face scanner library is not loaded");
}

async function loadNexoFaceModels() {
  if (!nexoFaceModelsPromise) {
    nexoFaceModelsPromise = waitForFaceApi().then(async faceapi => {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(FACE_MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(FACE_MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(FACE_MODEL_URL),
      ]);
      return faceapi;
    });
  }
  return nexoFaceModelsPromise;
}

function pointDistance(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function eyeScoreFromLandmarks(landmarks, box) {
  const leftEye = landmarks.getLeftEye();
  const rightEye = landmarks.getRightEye();
  if (leftEye.length < 6 || rightEye.length < 6 || !box?.width) return 0;

  const leftWidth = pointDistance(leftEye[0], leftEye[3]);
  const rightWidth = pointDistance(rightEye[0], rightEye[3]);
  const eyeGap = pointDistance(leftEye[3], rightEye[0]);
  const eyeLevelDiff = Math.abs(leftEye[0].y - rightEye[3].y);
  const widthScore = Math.min(100, ((leftWidth + rightWidth) / box.width) * 520);
  const gapScore = Math.min(100, (eyeGap / box.width) * 340);
  const levelScore = Math.max(0, 100 - (eyeLevelDiff / box.height) * 650);
  return Math.round(widthScore * 0.45 + gapScore * 0.25 + levelScore * 0.3);
}

export default function Nexo() {
  const token = localStorage.getItem(TOKEN_KEY);
  const currentUser = getLoginUser();
  const location = useLocation();
  const isAdmin = currentUser?.role === "admin";

  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [messageText, setMessageText] = useState("");
  const [error, setError] = useState("");
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [showPaymentPanel, setShowPaymentPanel] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [profileUploading, setProfileUploading] = useState(false);
  const [loginFace, setLoginFace] = useState(null);
  const [faceSaving, setFaceSaving] = useState(false);
  const faceVideoRef = useRef(null);
  const faceStreamRef = useRef(null);

  const [newGroup, setNewGroup] = useState({
    name: "",
    description: "",
    retention: "never",
    memberIds: [],
  });

  const [signal, setSignal] = useState({
    side: "BUY",
    symbol: "",
    entry: "",
    stopLoss: "",
    target: "",
    note: "",
  });

  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [chatMode, setChatMode] = useState("group");
  const [selectedDirectUserId, setSelectedDirectUserId] = useState("");
  const [showDirectModal, setShowDirectModal] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [newMemberId, setNewMemberId] = useState("");
  const [groupSettings, setGroupSettings] = useState({
    name: "",
    description: "",
    pinned: "",
    retention: "never",
  });
  const [directSettings, setDirectSettings] = useState({ retention: "never" });
  const [leftTab, setLeftTab] = useState("groups");
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [showTopMenu, setShowTopMenu] = useState(false);
  const [blockedMembers, setBlockedMembers] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [chatContextMenu, setChatContextMenu] = useState(null);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [selectedInfoUser, setSelectedInfoUser] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState("");

  const NEXO_API = window.location.hostname === "localhost"
    ? "http://localhost:5000/api/nexo"
    : "/api/nexo";

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const selectedGroup = groups.find(
    g => String(g.id) === String(selectedGroupId)
  );

  const alertsGroup = groups.find(group =>
    String(group.name || "").trim().toLowerCase() === "alerts"
  );

  const selectedDirectUser = users.find(
    u => String(u.id) === String(selectedDirectUserId)
  );

  const isNexoUser = currentUser?.role === "nexo";
  const subscriptionStatus = subscriptionInfo?.subscription?.status || "pending";
  const subscriptionPaid = !isNexoUser || subscriptionStatus === "paid";
  const profileName = profileUser?.fullName || currentUser?.fullName || profileUser?.username || currentUser?.username || "-";
  const profileUsername = profileUser?.username || currentUser?.username || "-";
  const profilePhone = profileUser?.phone || currentUser?.phone || "-";
  const profileEmail = profileUser?.email || currentUser?.email || "-";
  const profileImage = profileUser?.profileImage || currentUser?.profileImage || "";
  const activeMessageChatRef = useRef("");
  const seenMessageIdsRef = useRef(new Set());

  const filteredGroups = useMemo(() => {
    const q = search.toLowerCase().trim();

    const normalGroups = groups.filter(group =>
      String(group.name || "").trim().toLowerCase() !== "alerts"
    );

    if (!q) return normalGroups;

    return normalGroups.filter(
      g =>
        String(g.name || "").toLowerCase().includes(q) ||
        String(g.description || "").toLowerCase().includes(q)
    );
  }, [groups, search]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.toLowerCase().trim();

    return users.filter(u => {
      if (String(u.id) === String(currentUser?.id)) return false;
      if (currentUser?.role === "secret" && u.role !== "secret") return false;

      const text = `${u.username || ""} ${u.fullName || ""} ${u.phone || ""} ${u.email || ""}`.toLowerCase();
      return !q || text.includes(q);
    });
  }, [users, userSearch, currentUser]);

  const filteredChatUsers = useMemo(() => {
    const q = search.toLowerCase().trim();

    return users.filter(u => {
      if (String(u.id) === String(currentUser?.id)) return false;
      if (currentUser?.role === "secret" && u.role !== "secret") return false;

      const text = `${u.username || ""} ${u.fullName || ""} ${u.phone || ""} ${u.email || ""}`.toLowerCase();
      return !q || text.includes(q);
    });
  }, [users, search, currentUser]);

  const showAlertsChat =
    !!alertsGroup &&
    (!search.trim() || "alerts bloomberg support scanner alerts".includes(search.toLowerCase().trim()));

  const memberIds = new Set(members.map(m => String(m.id)));
  const blockedIds = new Set(blockedMembers.map(m => String(m.id)));

  const availableMembers = users.filter(
    u =>
      String(u.id) !== String(currentUser?.id) &&
      !memberIds.has(String(u.id)) &&
      !blockedIds.has(String(u.id))
  );

  const loadGroups = async () => {
    const res = await fetch("/api/nexo/groups", {
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      throw new Error(data.error || "Failed to load groups");
    }

    const loadedGroups = data.groups || [];
    setGroups(loadedGroups);

    const normalGroups = loadedGroups.filter(group =>
      String(group.name || "").trim().toLowerCase() !== "alerts"
    );

    if (!selectedGroupId && normalGroups[0]) {
      setSelectedGroupId(normalGroups[0].id);
    }
  };

  const loadUsers = async () => {
    const res = await fetch("/api/nexo/users", {
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      throw new Error(data.error || "Failed to load users");
    }

    setUsers(data.users || []);
  };

  const setLoadedMessagesWithAlert = (chatKey, nextMessages) => {
    const sameChat = activeMessageChatRef.current === chatKey;

    const hasNewIncoming =
      sameChat &&
      nextMessages.some(msg => {
        if (!msg?.id) return false;
        if (seenMessageIdsRef.current.has(msg.id)) return false;
        return String(msg.user?.id) !== String(currentUser?.id);
      });

    nextMessages.forEach(msg => {
      if (msg?.id) seenMessageIdsRef.current.add(msg.id);
    });

    activeMessageChatRef.current = chatKey;
    setMessages(nextMessages);

    if (hasNewIncoming) {
      playNotifyFeedback();
    }
  };

  const loadMessages = async groupId => {
    if (!groupId) return;

    const res = await fetch(
      `/api/nexo/groups/${groupId}/messages`,
      { headers: authHeaders }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      throw new Error(data.error || "Failed to load messages");
    }

    setLoadedMessagesWithAlert(`group-${groupId}`, data.messages || []);

    if (chatMode === "direct") {
      loadUsers().catch(() => {});
    }
  };

  const loadMembers = async groupId => {
    if (!groupId) return;

    const res = await fetch(
      `/api/nexo/groups/${groupId}/members`,
      { headers: authHeaders }
    );

    const data = await res.json().catch(() => ({}));

    if (res.ok && data.success) {
      setMembers(data.members || []);
    }
  };

  const loadBlockedMembers = async groupId => {
    if (!groupId || !isAdmin) return;

    const res = await fetch(
      `/api/nexo/groups/${groupId}/blocked`,
      { headers: authHeaders }
    );

    const data = await res.json().catch(() => ({}));

    if (res.ok && data.success) {
      setBlockedMembers(data.users || []);
    }
  };

  const loadDirectMessages = async userId => {
    if (!userId) return;

    const res = await fetch(
      `/api/nexo/direct/${userId}/messages`,
      { headers: authHeaders }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      throw new Error(data.error || "Failed to load direct messages");
    }

    setLoadedMessagesWithAlert(`direct-${userId}`, data.messages || []);
  };

  const loadDirectSettings = async userId => {
    if (!userId || !isAdmin) return;

    const res = await fetch(
      `/api/nexo/direct/${userId}/settings`,
      { headers: authHeaders }
    );

    const data = await res.json().catch(() => ({}));

    if (res.ok && data.success) {
      setDirectSettings(data.settings || { retention: "never" });
    }
  };

  const loadSubscriptionInfo = async () => {
    const res = await fetch("/api/subscription/me", {
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));

    if (res.ok && data.success) {
      setSubscriptionInfo(data);
      setPaymentDetails(data.paymentDetails || null);
    }
  };

  const loadProfile = async () => {
    const res = await fetch("/api/nexo/profile", {
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));

    if (res.ok && data.success) {
      const user = data.user || {};

      setProfileUser({
        ...currentUser,
        ...user,
        fullName: user.fullName || currentUser?.fullName || "",
        email: user.email || currentUser?.email || "",
        phone: user.phone || currentUser?.phone || "",
      });

      fetch("/api/face-login/me", { headers: authHeaders })
        .then(res => res.json())
        .then(faceData => {
          if (faceData.success) setLoginFace(faceData.face || null);
        })
        .catch(() => {});
    } else {
      setProfileUser(currentUser || null);
    }
  };

  useEffect(() => {
    loadGroups().catch(err => setError(err.message));
    loadUsers().catch(err => setError(err.message));
    loadSubscriptionInfo().catch(() => {});
    loadProfile().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return installNotifyFeedbackUnlock();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const chat = params.get("chat");

    if (chat !== "alerts" || !alertsGroup) return;

    setChatMode("group");
    setLeftTab("chats");
    setSelectedDirectUserId("");
    setSelectedGroupId(alertsGroup.id);
    setMessages([]);
  }, [alertsGroup, location.search]);

  useEffect(() => {
    if (chatMode === "group") {
      if (!selectedGroupId) return;

      loadMessages(selectedGroupId).catch(err => setError(err.message));
      loadMembers(selectedGroupId).catch(() => {});
      loadBlockedMembers(selectedGroupId).catch(() => {});

      const group = groups.find(g => String(g.id) === String(selectedGroupId));

      if (group) {
        setGroupSettings({
          name: group.name || "",
          description: group.description || "",
          pinned: group.pinned || "",
          retention: group.retention || "never",
        });
      }

      const timer = setInterval(() => {
        loadMessages(selectedGroupId).catch(() => {});
      }, 3000);

      return () => clearInterval(timer);
    }

    if (chatMode === "direct") {
      if (!selectedDirectUserId) return;

      loadDirectMessages(selectedDirectUserId).catch(err => setError(err.message));
      loadDirectSettings(selectedDirectUserId).catch(() => {});

      const timer = setInterval(() => {
        loadDirectMessages(selectedDirectUserId).catch(() => {});
      }, 3000);

      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, selectedDirectUserId, chatMode, groups]);

  const createGroup = async () => {
    setError("");

    try {
      const res = await fetch("/api/nexo/groups", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(newGroup),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Group create failed");
      }

      setShowGroupModal(false);
      setNewGroup({
        name: "",
        description: "",
        retention: "never",
        memberIds: [],
      });

      await loadGroups();
      setShowRightPanel(false);
      setChatMode("group");
      setLeftTab("groups");
      setSelectedGroupId(data.group.id);
    } catch (err) {
      setError(err.message || "Group create failed");
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim()) return;
    if (chatMode === "group" && !selectedGroupId) return;
    if (chatMode === "direct" && !selectedDirectUserId) return;
    if (chatMode === "group" && !isAdmin) {
      setError("Only admins can message in groups.");
      return;
    }

    const text = messageText;
    setMessageText("");
    setError("");

    try {
      const url =
        chatMode === "direct"
          ? `/api/nexo/direct/${selectedDirectUserId}/messages`
          : `/api/nexo/groups/${selectedGroupId}/messages`;

      const res = await fetch(url, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ text }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Message send failed");
      }

      if (chatMode === "direct") {
        await loadDirectMessages(selectedDirectUserId);
      } else {
        await loadMessages(selectedGroupId);
        await loadGroups();
      }
    } catch (err) {
      setError(err.message || "Message send failed");
    }
  };

  const sendSignal = async () => {
    if (!selectedGroupId) return;

    setError("");

    try {
      const res = await fetch(
        `/api/nexo/groups/${selectedGroupId}/signals`,
        {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(signal),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Signal send failed");
      }

      setSignal({
        side: "BUY",
        symbol: "",
        entry: "",
        stopLoss: "",
        target: "",
        note: "",
      });

      await loadMessages(selectedGroupId);
      await loadGroups();
    } catch (err) {
      setError(err.message || "Signal send failed");
    }
  };

  const openDirectChat = user => {
    setChatMode("direct");
    setLeftTab("chats");
    setSelectedGroupId("");
    setSelectedDirectUserId(user.id);
    setShowDirectModal(false);
    setMessages([]);
  };

  const messageAdminFromProfile = () => {
    const adminUser = users.find(user => user.role === "admin") || {
      id: "admin",
      username: "admin",
      fullName: "Admin",
      role: "admin",
    };

    setShowProfilePanel(false);
    openDirectChat(adminUser);
  };

  const compressProfileImage = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const image = new Image();

      reader.onload = () => {
        image.onload = () => {
          const maxSize = 600;
          const scale = Math.min(maxSize / image.width, maxSize / image.height, 1);
          const canvas = document.createElement("canvas");

          canvas.width = Math.round(image.width * scale);
          canvas.height = Math.round(image.height * scale);

          const ctx = canvas.getContext("2d");
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

          resolve(canvas.toDataURL("image/jpeg", 0.82));
        };

        image.onerror = () => reject(new Error("Image load failed"));
        image.src = reader.result;
      };

      reader.onerror = () => reject(new Error("Image read failed"));
      reader.readAsDataURL(file);
    });
  };

  const handleProfileImageUpload = event => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    setProfileUploading(true);

    compressProfileImage(file)
      .then(async profileImage => {
        const res = await fetch("/api/nexo/profile/photo", {
          method: "PUT",
          headers: authHeaders,
          body: JSON.stringify({ profileImage }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.success) {
          throw new Error(data.error || "Profile photo upload failed");
        }

        setProfileUser(data.user || null);
        await loadUsers();
      })
      .catch(err => {
        setError(err.message || "Profile photo upload failed");
      })
      .finally(() => {
        setProfileUploading(false);
        event.target.value = "";
      });
  };

  const captureLoginFaceScan = async video => {
    const faceapi = await loadNexoFaceModels();
    const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.65 });
    const detections = await faceapi
      .detectAllFaces(video, options)
      .withFaceLandmarks()
      .withFaceDescriptors();

    if (!detections.length) throw new Error("No real face detected. Keep your face centered.");
    if (detections.length > 1) throw new Error("Only one face is allowed for face login.");

    const scan = detections[0];
    const box = scan.detection.box;
    const detectionScore = scan.detection.score || 0;
    const eyeScore = eyeScoreFromLandmarks(scan.landmarks, box);
    const frameArea = video.videoWidth * video.videoHeight;
    const faceArea = box.width * box.height;

    if (detectionScore < 0.72) throw new Error("Face scan is not clear enough. Move closer.");
    if (eyeScore < 55) throw new Error("Both eyes must be clearly visible.");
    if (faceArea / frameArea < 0.08) throw new Error("Move closer to the camera.");

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

    return {
      image: canvas.toDataURL("image/jpeg", 0.72),
      descriptor: Array.from(scan.descriptor).map(value => Number(value.toFixed(6))),
      detectionScore,
      eyeScore,
      landmarkCount: scan.landmarks.positions.length,
      faceCount: detections.length,
    };
  };

  const startLoginFaceCamera = async () => {
    await loadNexoFaceModels();
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user", width: { ideal: 960 }, height: { ideal: 720 } },
      audio: false,
    });
    faceStreamRef.current = stream;
    if (faceVideoRef.current) {
      faceVideoRef.current.srcObject = stream;
      await faceVideoRef.current.play().catch(() => {});
    }
  };

  const stopLoginFaceCamera = () => {
    faceStreamRef.current?.getTracks?.().forEach(track => track.stop());
    faceStreamRef.current = null;
    if (faceVideoRef.current) faceVideoRef.current.srcObject = null;
  };

  const saveLoginFace = async () => {
    setFaceSaving(true);
    try {
      if (!faceStreamRef.current) await startLoginFaceCamera();
      await new Promise(resolve => setTimeout(resolve, 700));
      const video = faceVideoRef.current;
      if (!video?.videoWidth || !video?.videoHeight) throw new Error("Camera frame not ready");
      const faceScan = await captureLoginFaceScan(video);
      const res = await fetch("/api/face-login/me", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(faceScan),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || "Face save failed");
      setLoginFace(data.face);
    } catch (err) {
      setError(err.message || "Face save failed");
    } finally {
      setFaceSaving(false);
    }
  };

  const deleteLoginFace = async () => {
    const res = await fetch("/api/face-login/me", { method: "DELETE", headers: authHeaders });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      setError(data.error || "Face delete failed");
      return;
    }
    setLoginFace(null);
    stopLoginFaceCamera();
  };

  const openGroupChat = group => {
    setChatMode("group");
    setLeftTab("groups");
    setSelectedDirectUserId("");
    setSelectedGroupId(group.id);
    setMessages([]);
  };

  const openAlertsChat = () => {
    if (!alertsGroup) return;

    setChatMode("group");
    setLeftTab("chats");
    setSelectedDirectUserId("");
    setSelectedGroupId(alertsGroup.id);
    setMessages([]);
  };

  const openInfoPanel = () => {
  if (chatMode === "direct") {
    setSelectedInfoUser(selectedDirectUser || null);
  } else {
    setSelectedInfoUser(null);
  }

  setShowInfoPanel(true);
};

const openMemberInfo = (user) => {
  setSelectedInfoUser(user);
};


  const saveGroupSettings = async () => {
    setError("");
    if (!selectedGroupId) return;

    const res = await fetch(`/api/nexo/groups/${selectedGroupId}`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify(groupSettings),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      setError(data.error || "Group settings save failed");
      return;
    }

    await loadGroups();
    setShowRightPanel(false);
  };

  const addMember = async () => {
    setError("");

    if (!selectedGroupId) {
      setError("Select a group first");
      return;
    }

    if (!newMemberId) {
      setError("Select a member to add");
      return;
    }

    const res = await fetch(`/api/nexo/groups/${selectedGroupId}/members`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({ userId: newMemberId }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      setError(data.error || "Add member failed");
      return;
    }

    setNewMemberId("");
    await loadMembers(selectedGroupId);
    await loadGroups();
  };

  const removeMember = async userId => {
    if (!selectedGroupId) return;

    await fetch(`/api/nexo/groups/${selectedGroupId}/members/${userId}`, {
      method: "DELETE",
      headers: authHeaders,
    });

    await loadMembers(selectedGroupId);
  };

  const blockMember = async userId => {
    if (!selectedGroupId) return;

    await fetch(`/api/nexo/groups/${selectedGroupId}/blocked`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({ userId }),
    });

    await loadMembers(selectedGroupId);
    await loadBlockedMembers(selectedGroupId);
  };

  const unblockMember = async userId => {
    if (!selectedGroupId) return;

    await fetch(`/api/nexo/groups/${selectedGroupId}/blocked/${userId}`, {
      method: "DELETE",
      headers: authHeaders,
    });

    await loadBlockedMembers(selectedGroupId);
  };

  const saveDirectSettings = async () => {
    setError("");
    if (!selectedDirectUserId) return;

    const res = await fetch(`/api/nexo/direct/${selectedDirectUserId}/settings`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify(directSettings),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      setError(data.error || "Direct settings save failed");
      return;
    }

    setShowRightPanel(false);
  };

  const deleteMessage = async msg => {
    if (!msg?.id) return;

    const url =
      chatMode === "direct"
        ? `/api/nexo/direct/${selectedDirectUserId}/messages/${msg.id}`
        : `/api/nexo/groups/${selectedGroupId}/messages/${msg.id}`;

    const res = await fetch(url, {
      method: "DELETE",
      headers: authHeaders,
    });

    if (!res.ok) {
      setError("Delete failed");
      return;
    }

    setContextMenu(null);

    if (chatMode === "direct") {
      await loadDirectMessages(selectedDirectUserId);
    } else {
      await loadMessages(selectedGroupId);
    }
  };

    const deleteGroup = async groupId => {
    if (!groupId) return;
    if (!window.confirm("Delete this group permanently?")) return;

    const res = await fetch(`${NEXO_API}/groups/${groupId}`, {
      method: "DELETE",
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      setError(data.error || "Group delete failed");
      return;
    }

    setChatContextMenu(null);
    setMessages([]);
    setSelectedGroupId("");
    await loadGroups();
  };

  const clearGroupChatHistory = async groupId => {
    if (!groupId) return;
    if (!window.confirm("Clear all chat history from this group?")) return;

    const res = await fetch(`${NEXO_API}/groups/${groupId}/messages`, {
      method: "DELETE",
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      setError(data.error || "Clear group chat failed");
      return;
    }

    setChatContextMenu(null);

    if (String(selectedGroupId) === String(groupId)) {
      await loadMessages(groupId);
    }

    await loadGroups();
  };

  const deleteDirectChat = async userId => {
    if (!userId) return;
    if (!window.confirm("Delete this chat?")) return;

    const res = await fetch(`${NEXO_API}/direct/${userId}`, {
      method: "DELETE",
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      setError(data.error || "Direct chat delete failed");
      return;
    }

    setChatContextMenu(null);
    setMessages([]);

    if (String(selectedDirectUserId) === String(userId)) {
      setSelectedDirectUserId("");
    }

    await loadUsers();
  };

  const clearDirectChatHistory = async userId => {
    if (!userId) return;
    if (!window.confirm("Clear all chat history from this user?")) return;

    const res = await fetch(`${NEXO_API}/direct/${userId}/messages`, {
      method: "DELETE",
      headers: authHeaders,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      setError(data.error || "Clear direct chat failed");
      return;
    }

    setChatContextMenu(null);

    if (String(selectedDirectUserId) === String(userId)) {
      await loadDirectMessages(userId);
    }

    await loadUsers();
  };

  const openEditMessage = msg => {
    if (chatMode !== "group" || !isAdmin || msg?.kind !== "text") return;

    setEditingMessage(msg);
    setEditText(msg.text || "");
    setContextMenu(null);
  };

  const saveEditedMessage = async () => {
    if (!editingMessage?.id || !selectedGroupId) return;

    const text = editText.trim();
    if (!text) return;

    const res = await fetch(
      `/api/nexo/groups/${selectedGroupId}/messages/${editingMessage.id}`,
      {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ text }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      setError(data.error || "Message edit failed");
      return;
    }

    setEditingMessage(null);
    setEditText("");
    await loadMessages(selectedGroupId);
  };

  const toggleNewGroupMember = userId => {
    setNewGroup(prev => {
      const id = String(userId);
      const exists = prev.memberIds.includes(id);

      return {
        ...prev,
        memberIds: exists
          ? prev.memberIds.filter(item => item !== id)
          : [...prev.memberIds, id],
      };
    });
  };

  return (
    <div className="h-[calc(100vh-72px)] overflow-hidden bg-[#07111f] text-white">
      <div className="px-6 py-5 border-b border-slate-800 bg-[#0b1628]">
  <div className="flex items-center justify-between gap-5">
    <div>
      <div className="flex items-center gap-3">
        <Crown className="text-amber-300" size={26} />
        <h1 className="text-2xl font-bold">
          Welcome to "One Percent Club - NEXOVATE Groups"
        </h1>
      </div>

      <p className="text-slate-400 text-sm mt-1">
        Premium communication desk for market discussions, group updates, and signal broadcasts.
      </p>
    </div>

    <div className="nexo-menu relative shrink-0">
      <button
        type="button"
        onClick={() => setShowTopMenu(prev => !prev)}
        className="bg-[#111f33] hover:bg-[#172943] border border-slate-700 text-white px-4 py-3 rounded-lg flex items-center gap-3"
      >
        <Menu size={22} />
        <span className="font-semibold">Menu</span>
      </button>

      {showTopMenu && (
        <>
          <div
            className="fixed inset-0 z-40 bg-transparent"
            onClick={() => setShowTopMenu(false)}
          />

          <div className="absolute right-0 top-full mt-2 z-50 w-[240px] rounded-xl border border-slate-700 bg-[#0b1628] shadow-2xl overflow-hidden">
            
          <button
            type="button"
            onClick={() => {
              setShowProfilePanel(true);
              setShowTopMenu(false);
            }}
            className="w-full px-4 py-3 text-left hover:bg-[#111f33] flex items-center gap-3"
          >
            {profileImage ? (
              <img src={profileImage} alt="Profile" className="w-6 h-6 rounded-full object-cover" />
            ) : (
              <UserCircle size={18} />
            )}
            Profile
          </button>

          <button
            type="button"
            onClick={() => {
              setShowLeftPanel(prev => !prev);
              setShowTopMenu(false);
            }}
            className="w-full px-4 py-3 text-left hover:bg-[#111f33] flex items-center gap-3"
          >
            <Users size={18} />
            {showLeftPanel ? "Hide Groups / Chats" : "Show Groups / Chats"}
          </button>

          <button
            type="button"
            onClick={() => {
              setShowRightPanel(prev => !prev);
              setShowTopMenu(false);
            }}
            className="w-full px-4 py-3 text-left hover:bg-[#111f33] flex items-center gap-3"
          >
            <Settings size={18} />
            {showRightPanel ? "Hide Settings" : "Show Settings"}
          </button>

          <button
            type="button"
            onClick={() => {
              setShowDirectModal(true);
              setShowTopMenu(false);
            }}
            className="w-full px-4 py-3 text-left hover:bg-[#111f33] flex items-center gap-3"
          >
            <MessageCircle size={18} />
            Chat
          </button>

          {isNexoUser && (
            <button
              type="button"
              onClick={() => {
                setShowPaymentPanel(true);
                setShowTopMenu(false);
              }}
              className={`w-full px-4 py-3 text-left hover:bg-[#111f33] flex items-center gap-3 ${
                subscriptionPaid ? "text-green-300" : "text-yellow-300"
              }`}
            >
              {subscriptionPaid ? <CheckCircle size={18} /> : <Clock size={18} />}
              {subscriptionPaid ? "Paid" : "Payment Pending"}
            </button>
          )}

          <div className="px-4 py-3 text-emerald-300 bg-emerald-500/10 border-t border-slate-700 flex items-center gap-3">
            <Radio size={18} />
            Live Community
          </div>
        </div>
      </>
    )}
    </div>
  </div>
</div>

      <div
        className={`grid h-[calc(100vh-162px)] min-h-0 overflow-hidden ${
          showLeftPanel && showRightPanel
            ? "grid-cols-[320px_1fr_320px]"
            : showLeftPanel
            ? "grid-cols-[320px_1fr]"
            : showRightPanel
            ? "grid-cols-[1fr_320px]"
            : "grid-cols-1"
        }`}
      >
        {showLeftPanel && (

        <aside className="min-h-0 overflow-hidden border-r border-slate-800 bg-[#0b1628] flex flex-col">
          <button
            type="button"
            onClick={() => setShowLeftPanel(false)}
            title="Close"
            className="nexo-panel-close nexo-left-close rounded-full border border-slate-700 bg-[#111f33] p-2 text-slate-300 hover:text-white hover:bg-red-500"
          >
            <X size={16} />
          </button>

          <div className="p-4 border-b border-slate-800">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={leftTab === "groups" ? "Search groups" : "Search chats"}
                className="w-full bg-[#111f33] border border-slate-700 rounded-lg py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-400"
              />
            </div>

            {isAdmin && leftTab === "groups" && (
              <button
                onClick={() => setShowGroupModal(true)}
                className="mt-3 w-full bg-blue-500 hover:bg-blue-400 rounded-lg py-2 text-sm font-semibold flex items-center justify-center gap-2"
              >
                <Plus size={16} /> Create Group
              </button>
            )}
          </div>

          <div className="min-h-0 overflow-y-auto flex-1">
          {leftTab === "groups" && isNexoUser && !subscriptionPaid && (
            <div className="p-4 text-yellow-300 text-sm border-b border-slate-800">
              Payment pending. Complete renewal to view NEXO groups.
            </div>
          )}
            {leftTab === "groups" &&
              filteredGroups.map(group => (
                <button
                  key={group.id}
                  onClick={() => openGroupChat(group)}
                    onContextMenu={e => {
                      e.preventDefault();
                      setContextMenu(null);
                      setChatContextMenu({ x: e.clientX, y: e.clientY, type: "group", item: group });
                    }}
                  className={`w-full text-left p-4 border-b border-slate-800 hover:bg-[#064e3b]/70 ${
                    chatMode === "group" && String(selectedGroupId) === String(group.id)
                      ? "nexo-selected-row"
                      : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1 truncate font-semibold text-sm">{group.name}</div>
                    <div className="text-xs text-slate-500">{group.messageCount || 0}</div>
                  </div>
                  <div className="text-xs text-slate-400 mt-1 line-clamp-1">
                    {group.lastMessage?.kind === "signal"
                      ? "Signal posted"
                      : group.lastMessage?.text || group.description || "No messages yet"}
                  </div>
                </button>
              ))}

              {leftTab === "chats" && showAlertsChat && (
                <button
                  type="button"
                  onClick={openAlertsChat}
                  className={`w-full text-left p-4 border-b border-slate-800 hover:bg-[#064e3b]/70 ${
                    chatMode === "group" && String(selectedGroupId) === String(alertsGroup.id)
                      ? "nexo-selected-row"
                      : ""
                  }`}
                >
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <MessageCircle size={15} />
                    Alerts
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Bloomberg support scanner alerts
                  </div>
                </button>
              )}

            {leftTab === "chats" &&
              filteredChatUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => openDirectChat(user)}
                    onContextMenu={e => {
                      e.preventDefault();
                      setContextMenu(null);
                      setChatContextMenu({ x: e.clientX, y: e.clientY, type: "direct", item: user });
                    }}
                  className={`w-full text-left p-4 border-b border-slate-800 hover:bg-[#064e3b]/70 ${
                    chatMode === "direct" && String(selectedDirectUserId) === String(user.id)
                      ? "nexo-selected-row"
                      : ""
                  }`}
                >
                  <div className="min-w-0 font-semibold text-sm flex items-center gap-1">
                    <span className="min-w-0 truncate">
                      {user.username} {user.fullName ? `(${user.fullName})` : ""}
                    </span>
                    {Number(user.unreadCount || 0) > 0 && (
                      <span className="ml-2 bg-green-500 text-white text-[11px] rounded-full px-2 py-0.5">
                        +{user.unreadCount}
                      </span>
                    )}
                    {user.role === "nexo" && (
                      user.subscription?.status === "paid" ? (
                        <CheckCircle size={14} className="text-green-300" />
                      ) : (
                        <Clock size={14} className="text-yellow-300" />
                      )
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{user.role}</div>

                </button>
              ))}
          </div>

          <div className="border-t border-slate-800 grid grid-cols-2">
            <button
              onClick={() => setLeftTab("groups")}
              className={`py-3 text-sm flex items-center justify-center gap-2 ${
                leftTab === "groups" ? "text-green-300 bg-green-500/10" : "text-slate-400"
              }`}
            >
              <Users size={17} /> Groups
            </button>

            <button
              onClick={() => setLeftTab("chats")}
              className={`py-3 text-sm flex items-center justify-center gap-2 ${
                leftTab === "chats" ? "text-green-300 bg-green-500/10" : "text-slate-400"
              }`}
            >
              <MessageCircle size={17} /> Chats
            </button>
          </div>
        </aside>
)}
        <main className="min-h-0 overflow-hidden flex flex-col bg-[#07111f]">
          <div className="px-5 py-4 border-b border-slate-800 bg-[#0b1628]">
            <div className="nexo-topbar flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">
                  {chatMode === "direct"
                    ? `${selectedDirectUser?.username || "User"}${
                        selectedDirectUser?.fullName ? ` (${selectedDirectUser.fullName})` : ""
                      }`
                    : selectedGroup?.name || "Select Group"}
                </h2>
                <p className="text-xs text-slate-400">
                  {chatMode === "direct"
                    ? "Individual secure chat"
                    : selectedGroup?.description || "NEXOVATE secure communication channel"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={openInfoPanel}
                  className="text-xs text-slate-400 hover:text-white flex items-center gap-2"
                >
                  <Users size={15} /> {chatMode === "group" ? "Group" : "Direct"}
                </button>

                <button
                  type="button"
                  onClick={() => setShowRightPanel(prev => !prev)}
                  title="Show Settings"
                  className={`rounded border p-2 ${
                    showRightPanel
                      ? "bg-[#006b49] border-[#22c55e] text-white shadow-[0_0_14px_rgba(34,197,94,0.35)]"
                      : "border-slate-700 text-slate-400 hover:text-white"
                  }`}
                >
                  <Settings size={15} />
                </button>
              </div>

            </div>

            {chatMode === "group" && selectedGroup?.pinned && (
              <div className="mt-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
                <Pin size={14} /> {selectedGroup.pinned}
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {messages.map(msg => {
              const mine = String(msg.user?.id) === String(currentUser?.id);
              const isSignal = msg.kind === "signal";
              const directMessageRead =
                chatMode === "direct" &&
                mine &&
                selectedDirectUserId &&
                (msg.readBy || []).map(String).includes(String(selectedDirectUserId));

              return (
                <div
                  key={msg.id}
                  onContextMenu={e => {
                    e.preventDefault();
                    setContextMenu({ x: e.clientX, y: e.clientY, message: msg });
                  }}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[70%] rounded-xl px-4 py-3 ${
                      isSignal
                        ? "bg-[#101f35] border border-blue-500/40"
                        : mine
                        ? "bg-blue-600"
                        : "bg-[#111f33]"
                    }`}
                  >
                    <div className="text-xs text-slate-300 mb-1">
                      {msg.user?.fullName || msg.user?.username || "User"} - {timeText(msg.createdAt)}
                      {msg.editedAt && <span className="text-slate-400"> · edited</span>}
                    </div>

                    {isSignal ? (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <TrendingUp
                            size={18}
                            className={msg.signal?.side === "BUY" ? "text-emerald-300" : "text-red-300"}
                          />
                          <span
                            className={`text-lg font-bold ${
                              msg.signal?.side === "BUY" ? "text-emerald-300" : "text-red-300"
                            }`}
                          >
                            {msg.signal?.side} {msg.signal?.symbol}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div className="bg-slate-900/50 rounded p-2">
                            <div className="text-slate-500 text-xs">Entry</div>
                            <div>{msg.signal?.entry || "-"}</div>
                          </div>
                          <div className="bg-slate-900/50 rounded p-2">
                            <div className="text-slate-500 text-xs">SL</div>
                            <div>{msg.signal?.stopLoss || "-"}</div>
                          </div>
                          <div className="bg-slate-900/50 rounded p-2">
                            <div className="text-slate-500 text-xs">Target</div>
                            <div>{msg.signal?.target || "-"}</div>
                          </div>
                        </div>
                        {msg.signal?.note && (
                          <div className="text-sm text-slate-300 mt-3">{msg.signal.note}</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm whitespace-pre-wrap">{msg.text}</div>
                    )}

                    {chatMode === "direct" && mine && (
                      <div
                        className={`text-[10px] mt-1 text-right ${
                          directMessageRead
                            ? "text-green-300 drop-shadow-[0_0_8px_rgba(74,222,128,0.95)]"
                            : "text-blue-300"
                        }`}
                      >
                        {directMessageRead ? "\u2713\u2713 Read" : "\u2713 Not read"}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {!messages.length && (
              <div className="text-center text-slate-500 py-16">No messages yet.</div>
            )}
          </div>

          <div className="p-4 border-t border-slate-800 bg-[#0b1628]">
            {error && <div className="text-red-300 text-sm mb-2">{error}</div>}
            {chatMode === "group" && !isAdmin && (
              <div className="text-yellow-300 text-sm mb-2">
                Only admins can message in groups. You can still use individual chat.
              </div>
            )}

            <div className="nexo-actions flex items-center gap-3">
              <input
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") sendMessage();
                }}
                placeholder={chatMode === "group" && !isAdmin ? "Group messaging is admin only" : "Write a message..."}
                disabled={chatMode === "group" && !isAdmin}
                className="flex-1 bg-[#111f33] border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-blue-400 disabled:opacity-50"
              />
              <button
                onClick={sendMessage}
                disabled={chatMode === "group" && !isAdmin}
                className="bg-blue-500 hover:bg-blue-400 disabled:opacity-50 rounded-lg px-5 py-3 font-semibold flex items-center gap-2"
              >
                <Send size={17} /> Send
              </button>
            </div>
          </div>
        </main>

        {showRightPanel && (

        <aside className="border-l border-slate-800 bg-[#0b1628] p-4 overflow-y-auto">
          <button
            type="button"
            onClick={() => setShowRightPanel(false)}
            title="Close"
            className="nexo-panel-close nexo-right-close rounded-full border border-slate-700 bg-[#111f33] p-2 text-slate-300 hover:text-white hover:bg-red-500"
          >
            <X size={16} />
          </button>

          <div className="bg-[#111f33] border border-slate-700 rounded-xl p-4 mb-4">
            <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Community</div>
            <div className="text-lg font-bold">One Percent Club</div>
            <div className="text-sm text-slate-400 mt-1">NEXOVATE private groups</div>
          </div>

          {isAdmin && chatMode === "group" && selectedGroup && (
            <div className="bg-[#111f33] border border-slate-700 rounded-xl p-4 mb-4">
              <div className="font-semibold mb-3 flex items-center gap-2">
                <Settings size={16} /> Group Settings
              </div>

              <input
                value={groupSettings.name}
                onChange={e => setGroupSettings(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Group name"
                className="w-full bg-[#0b1628] border border-slate-700 rounded px-3 py-2 mb-2"
              />

              <textarea
                value={groupSettings.description}
                onChange={e => setGroupSettings(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description"
                className="w-full bg-[#0b1628] border border-slate-700 rounded px-3 py-2 mb-2 min-h-[70px]"
              />

              <input
                value={groupSettings.pinned}
                onChange={e => setGroupSettings(prev => ({ ...prev, pinned: e.target.value }))}
                placeholder="Pinned message"
                className="w-full bg-[#0b1628] border border-slate-700 rounded px-3 py-2 mb-2"
              />

              <select
                value={groupSettings.retention}
                onChange={e => setGroupSettings(prev => ({ ...prev, retention: e.target.value }))}
                className="w-full bg-[#0b1628] border border-slate-700 rounded px-3 py-2 mb-3"
              >
                <option value="never">No delete</option>
                <option value="24h">Delete in 24 hours</option>
              </select>

              <button
                onClick={saveGroupSettings}
                className="w-full bg-blue-500 hover:bg-blue-400 rounded py-2 font-semibold flex items-center justify-center gap-2 mb-4"
              >
                <Save size={15} /> Save Settings
              </button>

              <div className="font-semibold mb-2 flex items-center gap-2">
                <UserPlus size={15} /> Add Member
              </div>

              <div className="grid gap-2 mb-4">
                <select
                  value={newMemberId}
                  onChange={e => setNewMemberId(e.target.value)}
                  className="w-full min-w-0 bg-[#0b1628] border border-slate-700 rounded px-3 py-2"
                >
                  <option value="">Select user</option>
                  {availableMembers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username} {user.fullName ? `(${user.fullName})` : ""}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={addMember}
                  disabled={!newMemberId}
                  className="btn-solid-green w-full rounded px-4 py-2 font-semibold disabled:opacity-40"
                >
                  Add Member
                </button>
              </div>

              <div className="text-xs text-slate-400 mb-2">Members</div>

              <div className="space-y-2">
                {members.map(member => (
                  <div
                    key={member.id}
                    className="flex min-w-0 items-center justify-between gap-2 bg-[#0b1628] rounded px-3 py-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {member.username} {member.fullName ? `(${member.fullName})` : ""}
                    </span>

                    <div className="flex gap-2">
                      <button onClick={() => removeMember(member.id)} className="text-red-300">
                        <Trash2 size={15} />
                      </button>

                      <button onClick={() => blockMember(member.id)} className="text-amber-300">
                        <Ban size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-xs text-slate-400 mt-4 mb-2">Blocked Users</div>

              <div className="space-y-2">
                {blockedMembers.map(user => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between bg-red-500/10 rounded px-3 py-2"
                  >
                    <span className="text-sm">{user.username}</span>

                    <button onClick={() => unblockMember(user.id)} className="text-green-300 text-xs">
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isAdmin && chatMode === "direct" && selectedDirectUser && (
            <div className="bg-[#111f33] border border-slate-700 rounded-xl p-4 mb-4">
              <div className="font-semibold mb-3 flex items-center gap-2">
                <Settings size={16} /> Chat Settings
              </div>

              <select
                value={directSettings.retention}
                onChange={e => setDirectSettings({ retention: e.target.value })}
                className="w-full bg-[#0b1628] border border-slate-700 rounded px-3 py-2 mb-3"
              >
                <option value="never">No delete</option>
                <option value="24h">Delete in 24 hours</option>
              </select>

              <button
                onClick={saveDirectSettings}
                className="w-full bg-blue-500 hover:bg-blue-400 rounded py-2 font-semibold flex items-center justify-center gap-2"
              >
                <Save size={15} /> Save Chat Settings
              </button>
            </div>
          )}

          {isAdmin && chatMode === "group" && (
            <div className="bg-[#111f33] border border-slate-700 rounded-xl p-4">
              <div className="font-semibold mb-3">Signal Composer</div>

              <select
                value={signal.side}
                onChange={e => setSignal(prev => ({ ...prev, side: e.target.value }))}
                className="w-full bg-[#0b1628] border border-slate-700 rounded px-3 py-2 mb-2"
              >
                <option>BUY</option>
                <option>SELL</option>
              </select>

              {["symbol", "entry", "stopLoss", "target"].map(key => (
                <input
                  key={key}
                  value={signal[key]}
                  onChange={e => setSignal(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder={key === "stopLoss" ? "Stop Loss" : key.charAt(0).toUpperCase() + key.slice(1)}
                  className="w-full bg-[#0b1628] border border-slate-700 rounded px-3 py-2 mb-2"
                />
              ))}

              <textarea
                value={signal.note}
                onChange={e => setSignal(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Note"
                className="w-full bg-[#0b1628] border border-slate-700 rounded px-3 py-2 mb-3 min-h-[80px]"
              />

              <button
                onClick={sendSignal}
                disabled={!selectedGroupId}
                className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 rounded py-3 font-semibold"
              >
                Broadcast Signal
              </button>
            </div>
          )}
        </aside>
)}

      </div>

          {showInfoPanel && (
  <div className="fixed inset-0 bg-black/50 z-[70] flex justify-end">
    <div className="w-[380px] h-full bg-[#0b1628] border-l border-slate-700 p-5 overflow-y-auto">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold">
          {chatMode === "group" ? "Group Info" : "User Info"}
        </h2>
        <X className="cursor-pointer" onClick={() => setShowInfoPanel(false)} />
      </div>

      {chatMode === "group" && selectedGroup && (
        <>
          <div className="bg-[#111f33] border border-slate-700 rounded-xl p-4 mb-4">
            <div className="text-xs text-slate-400 mb-1">Group Name</div>
            <div className="font-semibold">{selectedGroup.name}</div>

            <div className="text-xs text-slate-400 mt-3 mb-1">Description</div>
            <div className="text-sm text-slate-300">
              {selectedGroup.description || "-"}
            </div>
          </div>

          <div className="text-sm font-semibold mb-2">
            Members ({members.length})
          </div>

          <div className="space-y-2 mb-4">
            {members.map(member => (
              <button
                key={member.id}
                onClick={() => openMemberInfo(member)}
                className="w-full text-left bg-[#111f33] hover:bg-[#172943] border border-slate-700 rounded-lg px-4 py-3"
              >
                <div className="font-semibold">
                  {member.username} {member.fullName ? `(${member.fullName})` : ""}
                </div>
                <div className="text-xs text-slate-400">{member.role}</div>
              </button>
            ))}

            {!members.length && (
              <div className="text-slate-500 text-sm text-center py-5">
                No members found.
              </div>
            )}
          </div>
        </>
      )}

      {chatMode === "direct" && selectedDirectUser && (
        <div className="bg-[#111f33] border border-slate-700 rounded-xl p-4 mb-4">
          <div className="text-xs text-slate-400 mb-1">Username</div>
          <div className="font-semibold">{selectedDirectUser.username}</div>

          <div className="text-xs text-slate-400 mt-3 mb-1">Full Name</div>
          <div>{selectedDirectUser.fullName || "-"}</div>

          <div className="text-xs text-slate-400 mt-3 mb-1">Phone</div>
          <div>{selectedDirectUser.phone || "-"}</div>

          <div className="text-xs text-slate-400 mt-3 mb-1">Email</div>
          <div>{selectedDirectUser.email || "-"}</div>

          <div className="text-xs text-slate-400 mt-3 mb-1">Role</div>
          <div>{selectedDirectUser.role || "-"}</div>
        </div>
      )}

      {selectedInfoUser && (
        <div className="bg-[#111f33] border border-blue-500/30 rounded-xl p-4">
          <div className="text-sm font-semibold mb-3">Selected Member Details</div>

          <div className="text-xs text-slate-400 mb-1">Username</div>
          <div className="font-semibold">{selectedInfoUser.username}</div>

          <div className="text-xs text-slate-400 mt-3 mb-1">Full Name</div>
          <div>{selectedInfoUser.fullName || "-"}</div>

          <div className="text-xs text-slate-400 mt-3 mb-1">Phone</div>
          <div>{selectedInfoUser.phone || "-"}</div>

          <div className="text-xs text-slate-400 mt-3 mb-1">Email</div>
          <div>{selectedInfoUser.email || "-"}</div>

          <div className="text-xs text-slate-400 mt-3 mb-1">Role</div>
          <div>{selectedInfoUser.role || "-"}</div>
        </div>
      )}
    </div>
  </div>
)}


      {editingMessage && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[75]">
          <div className="bg-[#0b1628] border border-slate-700 rounded-xl w-[520px] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Edit Message</h2>
              <X
                className="cursor-pointer"
                onClick={() => {
                  setEditingMessage(null);
                  setEditText("");
                }}
              />
            </div>

            <textarea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              className="w-full min-h-[130px] bg-[#111f33] border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-blue-400"
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setEditingMessage(null);
                  setEditText("");
                }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded"
              >
                Cancel
              </button>

              <button
                onClick={saveEditedMessage}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-400 rounded font-semibold flex items-center gap-2"
              >
                <Save size={16} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

            {chatContextMenu && (
        <div
          className="fixed z-[90] bg-[#111f33] border border-slate-700 rounded-lg shadow-xl overflow-hidden"
          style={{ top: chatContextMenu.y, left: chatContextMenu.x }}
        >
          {chatContextMenu.type === "group" && isAdmin && (
            <>
              <button
                onClick={() => deleteGroup(chatContextMenu.item.id)}
                className="px-4 py-2 text-sm text-red-300 hover:bg-red-500/10 flex items-center gap-2 w-full text-left"
              >
                <Trash2 size={15} /> Delete Group
              </button>

              <button
                onClick={() => clearGroupChatHistory(chatContextMenu.item.id)}
                className="px-4 py-2 text-sm text-yellow-300 hover:bg-yellow-500/10 flex items-center gap-2 w-full text-left"
              >
                <Trash2 size={15} /> Clear Chat History
              </button>
            </>
          )}

          {chatContextMenu.type === "direct" && (
            <>
              <button
                onClick={() => deleteDirectChat(chatContextMenu.item.id)}
                className="px-4 py-2 text-sm text-red-300 hover:bg-red-500/10 flex items-center gap-2 w-full text-left"
              >
                <Trash2 size={15} /> Delete Chat
              </button>

              <button
                onClick={() => clearDirectChatHistory(chatContextMenu.item.id)}
                className="px-4 py-2 text-sm text-yellow-300 hover:bg-yellow-500/10 flex items-center gap-2 w-full text-left"
              >
                <Trash2 size={15} /> Clear Chat History
              </button>
            </>
          )}

          <button
            onClick={() => setChatContextMenu(null)}
            className="px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 w-full text-left"
          >
            Cancel
          </button>
        </div>
      )}

      {contextMenu && (
        <div
          className="fixed z-[80] bg-[#111f33] border border-slate-700 rounded-lg shadow-xl overflow-hidden"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {chatMode === "group" && isAdmin && contextMenu.message?.kind === "text" && (
            <button
              onClick={() => openEditMessage(contextMenu.message)}
              className="px-4 py-2 text-sm text-blue-300 hover:bg-blue-500/10 flex items-center gap-2 w-full text-left"
            >
              <Save size={15} /> Edit Message
            </button>
          )}

          <button
            onClick={() => deleteMessage(contextMenu.message)}
            disabled={chatMode === "group" && !isAdmin}
            className="px-4 py-2 text-sm text-red-300 hover:bg-red-500/10 disabled:opacity-40 flex items-center gap-2"
          >
            <Trash2 size={15} /> Delete Chat
          </button>

          <button
            onClick={() => setContextMenu(null)}
            className="px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 w-full text-left"
          >
            Cancel
          </button>
        </div>
      )}

      {showGroupModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0b1628] border border-slate-700 rounded-xl w-[480px] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Create Group</h2>
              <X className="cursor-pointer" onClick={() => setShowGroupModal(false)} />
            </div>

            <input
              value={newGroup.name}
              onChange={e => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Group name"
              className="w-full bg-[#111f33] border border-slate-700 rounded px-3 py-3 mb-3"
            />

            <textarea
              value={newGroup.description}
              onChange={e => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description"
              className="w-full bg-[#111f33] border border-slate-700 rounded px-3 py-3 mb-3 min-h-[90px]"
            />

            <select
              value={newGroup.retention}
              onChange={e => setNewGroup(prev => ({ ...prev, retention: e.target.value }))}
              className="w-full bg-[#111f33] border border-slate-700 rounded px-3 py-3 mb-3"
            >
              <option value="never">No delete</option>
              <option value="24h">Delete in 24 hours</option>
            </select>

            <div className="text-sm font-semibold mb-2">Initial Members</div>
            <div className="max-h-[180px] overflow-y-auto border border-slate-700 rounded-lg p-2 mb-4">
              {users
                .filter(user => String(user.id) !== String(currentUser?.id))
                .map(user => (
                  <label
                    key={user.id}
                    className="flex items-center gap-2 px-2 py-2 hover:bg-[#111f33] rounded"
                  >
                    <input
                      type="checkbox"
                      checked={newGroup.memberIds.includes(String(user.id))}
                      onChange={() => toggleNewGroupMember(user.id)}
                    />
                    <span>
                      {user.username} {user.fullName ? `(${user.fullName})` : ""}
                    </span>
                  </label>
                ))}
            </div>

            <button
              onClick={createGroup}
              className="w-full bg-blue-500 hover:bg-blue-400 rounded py-3 font-semibold"
            >
              Create Group
            </button>
          </div>
        </div>
      )}

      {showDirectModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0b1628] border border-slate-700 rounded-xl w-[460px] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Start Individual Chat</h2>
              <X className="cursor-pointer" onClick={() => setShowDirectModal(false)} />
            </div>

            <input
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="Search username, full name, phone, email"
              className="w-full bg-[#111f33] border border-slate-700 rounded px-3 py-3 mb-4"
            />

            <div className="max-h-[360px] overflow-y-auto space-y-2">
              {filteredUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => openDirectChat(user)}
                  className="w-full text-left bg-[#111f33] hover:bg-[#172943] border border-slate-700 rounded-lg px-4 py-3"
                >
                  <div className="font-semibold">
                    {user.username} {user.fullName ? `(${user.fullName})` : ""}
                  </div>
                  <div className="text-xs text-slate-400">{user.role}</div>
                </button>
              ))}

              {!filteredUsers.length && (
                <div className="text-center text-slate-500 py-8">No users found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {showProfilePanel && (
        <div className="nexo-modal fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="nexo-modal-box bg-[#0b1628] border border-slate-700 rounded-xl w-[520px] max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <UserCircle size={22} /> Profile
              </h2>
              <X className="cursor-pointer" onClick={() => setShowProfilePanel(false)} />
            </div>

            <div className="flex items-center gap-4 mb-5">
              <div className="w-24 h-24 rounded-full bg-[#111f33] border border-slate-700 overflow-hidden flex items-center justify-center">
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserCircle size={54} className="text-slate-500" />
                )}
              </div>

              <div>
                <label className="bg-blue-500 hover:bg-blue-400 rounded px-4 py-2 font-semibold flex items-center gap-2 cursor-pointer w-fit">
                  <Camera size={16} />
                  {profileUploading ? "Uploading..." : "Update Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfileImageUpload}
                    disabled={profileUploading}
                  />
                </label>
                <div className="text-xs text-slate-400 mt-2">
                  Upload from files or phone gallery.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-[#111f33] border border-slate-700 rounded-lg p-3">
                <div className="text-slate-400 text-xs mb-1">Name</div>
                <div className="font-semibold">{profileName}</div>
              </div>

              <div className="bg-[#111f33] border border-slate-700 rounded-lg p-3">
                <div className="text-slate-400 text-xs mb-1">Username</div>
                <div>{profileUsername}</div>
              </div>

              <div className="bg-[#111f33] border border-slate-700 rounded-lg p-3">
                <div className="text-slate-400 text-xs mb-1">Phone Number</div>
                <div>{profilePhone}</div>
              </div>

              <div className="bg-[#111f33] border border-slate-700 rounded-lg p-3">
                <div className="text-slate-400 text-xs mb-1">Email</div>
                <div>{profileEmail}</div>
              </div>

              <div className="bg-[#111f33] border border-slate-700 rounded-lg p-3">
                <div className="text-slate-400 text-xs mb-1">Payment Status</div>
                {isNexoUser ? (
                  <div className={subscriptionPaid ? "text-green-300 flex items-center gap-1" : "text-yellow-300 flex items-center gap-1"}>
                    {subscriptionPaid ? <CheckCircle size={15} /> : <Clock size={15} />}
                    {subscriptionPaid ? "Paid" : "Pending Payment"}
                  </div>
                ) : (
                  <div className="text-slate-300">Not Applicable</div>
                )}
              </div>

              <div className="bg-[#111f33] border border-slate-700 rounded-lg p-3">
                <div className="text-slate-400 text-xs mb-1">Payment Date</div>
                <div>
                  {subscriptionInfo?.subscription?.paidAt
                    ? new Date(subscriptionInfo.subscription.paidAt).toLocaleDateString()
                    : "-"}
                </div>
              </div>

              <div className="bg-[#111f33] border border-slate-700 rounded-lg p-3 col-span-2">
                <div className="text-slate-400 text-xs mb-1">Next Renewal Date</div>
                <div>
                  {subscriptionInfo?.subscription?.nextDueAt
                    ? new Date(subscriptionInfo.subscription.nextDueAt).toLocaleDateString()
                    : "-"}
                </div>
              </div>
            </div>

            <div className="bg-[#111f33] border border-slate-700 rounded-lg p-3 col-span-2">
              <div className="text-slate-400 text-xs mb-2">Login With Face</div>
              <video ref={faceVideoRef} autoPlay muted playsInline className="w-full rounded mb-3 bg-black" style={{ height: 170, objectFit: "cover" }} />
              {loginFace?.image && (
                <div className="flex items-center gap-3 mb-3">
                  <img src={loginFace.image} alt="Login face" className="w-20 h-14 object-cover rounded border border-slate-600" />
                  <div className="text-xs text-slate-300">
                    <div>Face saved</div>
                    <div>{loginFace.updatedAt ? new Date(loginFace.updatedAt).toLocaleString() : ""}</div>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={startLoginFaceCamera} className="bg-slate-700 rounded px-3 py-2 text-xs">Start Camera</button>
                <button type="button" onClick={saveLoginFace} disabled={faceSaving} className="bg-blue-500 rounded px-3 py-2 text-xs font-semibold">
                  {faceSaving ? "Saving..." : loginFace ? "Replace Face" : "Add Face"}
                </button>
                {loginFace && (
                  <button type="button" onClick={deleteLoginFace} className="bg-red-700 rounded px-3 py-2 text-xs font-semibold">Remove Face</button>
                )}
              </div>
            </div>       

            <button
              onClick={messageAdminFromProfile}
              className="mt-5 w-full bg-[#111f33] border border-blue-500/40 hover:border-blue-300 rounded-lg px-4 py-3 text-sm text-slate-300"
            >
              If any changes - message{" "}
              <span className="font-bold text-blue-300 drop-shadow-[0_0_8px_rgba(96,165,250,0.9)]">
                ADMIN
              </span>
            </button>
          </div>
        </div>
      )}

        {showPaymentPanel && (
  <div className="nexo-modal fixed inset-0 bg-black/60 flex items-center justify-center z-50">
    <div className="nexo-modal-box bg-[#0b1628] border border-slate-700 rounded-xl w-[480px] p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <CreditCard size={20} /> NEXO Payment
        </h2>
        <X className="cursor-pointer" onClick={() => setShowPaymentPanel(false)} />
      </div>

      <div className="space-y-3 text-sm">
        <div className="bg-[#111f33] rounded-lg p-3">
          <div className="text-slate-400 text-xs">Status</div>
          <div className={subscriptionPaid ? "text-green-300" : "text-yellow-300"}>
            {subscriptionPaid ? "Paid" : "Pending Payment"}
          </div>
        </div>

        {subscriptionInfo?.subscription?.nextDueAt && (
          <div className="bg-[#111f33] rounded-lg p-3">
            <div className="text-slate-400 text-xs">Next Renewal</div>
            <div>{new Date(subscriptionInfo.subscription.nextDueAt).toLocaleDateString()}</div>
          </div>
        )}

        {paymentDetails?.upiId && (
          <div className="bg-[#111f33] rounded-lg p-3">
            <div className="text-slate-400 text-xs">UPI ID</div>
            <div className="font-semibold text-blue-300">{paymentDetails.upiId}</div>
          </div>
        )}

        {paymentDetails?.qrImageUrl && (
          <div className="bg-[#111f33] rounded-lg p-3">
            <div className="text-slate-400 text-xs mb-2">UPI QR</div>
            <img
              src={paymentDetails.qrImageUrl}
              alt="Payment QR"
              className="max-h-56 rounded-lg mx-auto"
            />
          </div>
        )}

        {(paymentDetails?.bankName || paymentDetails?.accountNumber) && (
          <div className="bg-[#111f33] rounded-lg p-3 space-y-1">
            <div className="text-slate-400 text-xs">Bank Details</div>
            <div>Account Name: {paymentDetails.accountName || "-"}</div>
            <div>Bank: {paymentDetails.bankName || "-"}</div>
            <div>Account No: {paymentDetails.accountNumber || "-"}</div>
            <div>IFSC: {paymentDetails.ifsc || "-"}</div>
          </div>
        )}

        {paymentDetails?.instructions && (
          <div className="bg-[#111f33] rounded-lg p-3">
            <div className="text-slate-400 text-xs">Instructions</div>
            <div>{paymentDetails.instructions}</div>
          </div>
        )}
      </div>
    </div>
  </div>
)}

    </div>
  );
}
